import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export default function Requirement() {
  const [heading, setHeading] = useState('');
  const [projectOverview, setProjectOverview] = useState('');
  const [processFlow, setProcessFlow] = useState('');
  const [coreFeatures, setCoreFeatures] = useState('');
  const [technicalRequirements, setTechnicalRequirements] = useState('');
  const [uiRequirements, setUiRequirements] = useState('');
  const [message, setMessage] = useState('');
  const [savedFileUrl, setSavedFileUrl] = useState('');
  const [savedRecordId, setSavedRecordId] = useState('');
  const [quotationUrl, setQuotationUrl] = useState('');
  const [quotationStatus, setQuotationStatus] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [customerRequirements, setCustomerRequirements] = useState([]);

  const fetchCustomerRequirements = async () => {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8000' : '');
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const resp = await fetch(apiBase + '/api/requirements/my/list', { headers });
      const data = await resp.json();
      if (resp.ok && data && Array.isArray(data.items)) {
        setCustomerRequirements(data.items);
      }
    } catch (e) {
      // ignore
    }
  };

  // If page contains ?id=..., fetch the record so customers can download quotation
  const fetchRequirement = async (idParam) => {
    try {
      const params = new URLSearchParams(location.search);
      const id = idParam || params.get('id');
      const apiBase = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8000' : '');

      let resp;
      let data;

      if (id) {
        resp = await fetch(apiBase + '/api/requirements/' + id);
        data = await resp.json();
      } else {
        // no id provided: try to fetch the current authenticated customer's latest requirement
        const token = localStorage.getItem('token');
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        resp = await fetch(apiBase + '/api/requirements/my', { headers });
        data = await resp.json();
      }

      if (resp.ok && data && data.item) {
        const item = data.item;
        console.log('Requirement fetched:', item._id, item.quotationStatus);
        setHeading(item.heading || '');
        setProjectOverview(item.projectOverview || '');
        setProcessFlow(item.processFlow || '');
        setCoreFeatures(item.coreFeatures || '');
        setTechnicalRequirements(item.technicalRequirements || '');
        setUiRequirements(item.uiRequirements || '');
        setSavedFileUrl((apiBase || '') + (item.filePath || ''));
        setSavedRecordId(item._id || '');
        if (item.quotationUrl) setQuotationUrl(item.quotationUrl);
        if (item.quotationStatus) {
          const normalized = String(item.quotationStatus).toLowerCase().trim();
          setQuotationStatus(normalized);
          if (normalized === 'accepted') {
            setActiveTab('clientform');
            try { localStorage.setItem('hasAcceptedRequirement', 'true'); } catch (e) { }
          } else {
            try { localStorage.setItem('hasAcceptedRequirement', 'false'); } catch (e) { }
          }
        }
        return item;
      }
      try { localStorage.setItem('hasAcceptedRequirement', 'false'); } catch (e) { }
      return null;
    } catch (err) {
      try { localStorage.setItem('hasAcceptedRequirement', 'false'); } catch (e) { }
      return null;
    }
  };

  useEffect(() => {
    // initial load
    fetchRequirement();
    // fetch the customer's requirement list for quick access
    fetchCustomerRequirements();
  }, []);

  // Re-run fetch when the SPA URL query changes (e.g. ?id=...) so the page
  // updates if a user opens a notification or navigation changes the id.
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const id = params.get('id');
      if (id) fetchRequirement(id);
    } catch (err) {
      // ignore
    }
  }, [location.search]);

  // If quotationStatus changes to 'accepted', open the Add Client tab
  useEffect(() => {
    if ((quotationStatus || '').toLowerCase() === 'accepted') {
      setActiveTab('accepted');
    }
  }, [quotationStatus]);

  // When the Requirement tab (Details) is active, fetch the single requirement
  // by id to get the authoritative `quotationStatus`. This is simpler and
  // avoids pulling the whole list.
  useEffect(() => {
    const ensureRequirementStatus = async () => {
      try {
        if (activeTab !== 'details') return;
        const params = new URLSearchParams(location.search);
        const id = savedRecordId || params.get('id');
        if (!id) return;
        const item = await fetchRequirement(id);
        if (item) {
          const qs = String(item.quotationStatus || '').toLowerCase();
          console.log('[Requirement] fetched status for', id, qs);
          setQuotationStatus(qs);
          if (qs === 'accepted') {
            setActiveTab('clientform');
            try { localStorage.setItem('hasAcceptedRequirement', 'true'); } catch (e) { }
          } else {
            try { localStorage.setItem('hasAcceptedRequirement', 'false'); } catch (e) { }
          }
        }
      } catch (err) {
        console.warn('Error ensuring requirement status:', err);
      }
    };

    ensureRequirementStatus();
  }, [activeTab, location.search, savedRecordId]);

  const handleRefresh = async () => {
    const params = new URLSearchParams(window.location.search);
    const id = savedRecordId || params.get('id');
    if (!id) {
      setMessage('No requirement id to refresh');
      setTimeout(() => setMessage(''), 2000);
      return;
    }
    const updated = await fetchRequirement(id);
    if (updated) setMessage('Refreshed requirement');
    setTimeout(() => setMessage(''), 2000);
  };

  // Polling + storage event: if the notification accept/decline happens elsewhere, refresh this page
  useEffect(() => {
    let pollTimer = null;

    const startPolling = () => {
      // poll every 5 seconds until accepted or no record id
      pollTimer = setInterval(async () => {
        if (!savedRecordId) return;
        try {
          const updated = await fetchRequirement(savedRecordId);
          if (updated && updated.quotationStatus === 'accepted') {
            // stop polling and optionally switch to addclient tab
            clearInterval(pollTimer);
            pollTimer = null;
            setActiveTab('addclient');
            setMessage('Quotation accepted — you may add a client now.');
            setTimeout(() => setMessage(''), 4000);
          }
        } catch (e) {
          // ignore
        }
      }, 5000);
    };

    const storageHandler = (ev) => {
      if (!ev.key) return;
      if (!savedRecordId) return;
      if (ev.key === `requirement_update_${savedRecordId}`) {
        // fetch immediately when we see an update
        fetchRequirement(savedRecordId).then((updated) => {
          if (updated && updated.quotationStatus === 'accepted') {
            setActiveTab('addclient');
            setMessage('Quotation accepted — you may add a client now.');
            setTimeout(() => setMessage(''), 4000);
          }
        });
      }
    };

    window.addEventListener('storage', storageHandler);
    startPolling();

    return () => {
      window.removeEventListener('storage', storageHandler);
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [savedRecordId]);

  // If the user switches browser tabs or the window regains focus, re-fetch
  // the requirement so the Debug panel and UI reflect the latest server state.
  useEffect(() => {
    const handler = () => {
      try {
        const params = new URLSearchParams(location.search);
        const id = savedRecordId || params.get('id');
        if (id) fetchRequirement(id);
      } catch (e) {
        // ignore
      }
    };

    const visibilityHandler = () => {
      if (!document.hidden) handler();
    };

    window.addEventListener('focus', handler);
    document.addEventListener('visibilitychange', visibilityHandler);

    return () => {
      window.removeEventListener('focus', handler);
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
  }, [location.search, savedRecordId]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('requirementsData');
      if (raw) {
        const data = JSON.parse(raw);
        setHeading(data.heading || '');
        setProjectOverview(data.projectOverview || '');
        setProcessFlow(data.processFlow || '');
        setCoreFeatures(data.coreFeatures || '');
        setTechnicalRequirements(data.technicalRequirements || '');
        setUiRequirements(data.uiRequirements || '');
      }
    } catch (err) {
      // ignore and use defaults
    }
  }, []);

  const onSave = async (isSubmit = false) => {
    const payload = {
      heading,
      projectOverview,
      processFlow,
      coreFeatures,
      technicalRequirements,
      uiRequirements,
    };

    try {
      localStorage.setItem('requirementsData', JSON.stringify(payload));
      setMessage(isSubmit ? 'Submitting...' : 'Saved locally.');

      if (isSubmit) {
        try {
          const apiBase = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8000' : '');
          const url = apiBase + '/api/requirements/save';
          const token = localStorage.getItem('token');
          const headers = { 'Content-Type': 'application/json' };
          if (token) headers['Authorization'] = `Bearer ${token}`;
          const resp = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
          });
          const data = await resp.json();
          if (resp.ok && data && data.file) {
            const fullUrl = (apiBase || '') + data.file;
            setSavedFileUrl(fullUrl);
            setSavedRecordId(data.id || data._id || '');
            setMessage('Submitted and saved on server.');
            // refresh the customer's requirement list so the new item appears
            try { fetchCustomerRequirements(); } catch (e) { }
            setTimeout(() => setMessage(''), 3000);
            return { success: true, data };
          } else {
            setSavedFileUrl('');
            setMessage('Submitted locally, server save failed.');
            setTimeout(() => setMessage(''), 3000);
            return { success: false, data };
          }
        } catch (err) {
          setSavedFileUrl('');
          setMessage('Submitted locally, server save failed.');
          setTimeout(() => setMessage(''), 3000);
          return { success: false, error: err };
        }
      }

      setTimeout(() => setMessage(''), 3000);
      return { success: true };
    } catch (err) {
      setMessage('Failed to save.');
      setTimeout(() => setMessage(''), 3000);
      return { success: false, error: err };
    }
  };

  

  return (
    <div className="p-6 bg-white rounded shadow">
      {/* Tabs: Details + Add Client (shown when quotation accepted) */}
      <div className="mb-4">
        <div className="flex border-b">
          <button
            className={`px-4 py-2 -mb-px ${activeTab === 'details' ? 'border-b-2 border-blue-600' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          {(quotationStatus || '').toLowerCase() === 'accepted' && (
              <button
                className={`px-4 py-2 -mb-px ${activeTab === 'accepted' ? 'border-b-2 border-blue-600' : ''}`}
                onClick={() => setActiveTab('accepted')}
              >
                Add Client
              </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-bold">{heading}</h1>
        <div>
          <button
            onClick={() => setShowModal(true)}
            className="ml-4 px-3 py-2 bg-indigo-600 text-white rounded"
          >
            Requirement Detail
          </button>
        </div>
      </div>

      {message && <div className="mb-4 text-sm text-green-600">{message}</div>}

      {activeTab === 'accepted' ? (
        <div className="p-4 bg-yellow-50 rounded">
          <h2 className="text-xl font-semibold mb-2">Quotation Accepted</h2>
          <p className="mb-4">The quotation has been accepted. To add this client to your workspace, please use the Clients page.</p>
          <button
            onClick={() => { window.location.href = '/admin/clients'; }}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            Go to Clients
          </button>
        </div>
      ) : (
        <div>
          {/* Modal: shows the Details form in a popup when requested */}
          {showModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black opacity-40" onClick={() => setShowModal(false)} />
              <div className="relative max-w-3xl w-full mx-4 bg-white rounded shadow-lg p-6 z-10 max-h-screen overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Requirement Details</h2>
                  <button onClick={() => setShowModal(false)} className="text-gray-600 hover:text-gray-900">Close</button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block font-semibold mb-1">Heading</label>
                    <input value={heading} placeholder="Requirement" onChange={(e) => setHeading(e.target.value)} className="w-full border px-3 py-2 rounded" />
                  </div>

                  <div>
                    <label className="block font-semibold mb-1">Project Overview</label>
                    <textarea value={projectOverview} placeholder={"Build a web-based tool that automates the full client workflow from initial inquiry to contract termination. All steps are trackable, status-based, and auto-organized with notifications, document generation and audit logs."} onChange={(e) => setProjectOverview(e.target.value)} rows={4} className="w-full border px-3 py-2 rounded" />
                  </div>

                  <div>
                    <label className="block font-semibold mb-1">Process Flow</label>
                    <textarea value={processFlow} placeholder={"1. Preliminary Requirement Analysis\n2. Quotation Generation\n3. Client Onboarding Form\n4. Project Assessment (Login access check)\n5. Contract Creation (Scope of Work, Estimated Time)\n6. Invoice Generation\n7. Payment Update\n8. Revised Contract (if needed)\n9. Contract Termination Notice\n10. Feedback Collection"} onChange={(e) => setProcessFlow(e.target.value)} rows={6} className="w-full border px-3 py-2 rounded" />
                    <p className="text-xs text-gray-500 mt-1">Use newline-separated steps (each step on a new line or prefixed with a number).</p>
                  </div>

                  <div>
                    <label className="block font-semibold mb-1">Core Features Needed</label>
                    <textarea value={coreFeatures} placeholder={"Client Management (CRUD, profile, timeline)\nStep-by-step automation with statuses and reminders\nDocument auto-generation (quotation, contract, invoice, termination)\nDashboard and analytics\nRole-based access (Admin, Staff, Client)"} onChange={(e) => setCoreFeatures(e.target.value)} rows={5} className="w-full border px-3 py-2 rounded" />
                  </div>

                  <div>
                    <label className="block font-semibold mb-1">Technical Requirements</label>
                    <textarea value={technicalRequirements} placeholder={"Backend: Node.js + Express\nDatabase: MongoDB (schemas for Client, Stage, Documents, Templates)\nBackground jobs: node-cron / queue (BullMQ) for automation\nPDF generation: Puppeteer or html-pdf\nStorage: local uploads (MVP) or S3 (production)\nNotifications: Email (Nodemailer) + optional SMS/WhatsApp"} onChange={(e) => setTechnicalRequirements(e.target.value)} rows={5} className="w-full border px-3 py-2 rounded" />
                  </div>

                  <div>
                    <label className="block font-semibold mb-1">User Interface Requirements</label>
                    <textarea value={uiRequirements} placeholder={"Admin dashboard with cards and quick filters\nClient profile page with process timeline and stage detail\nTemplate editor (WYSIWYG or HTML)\nClient portal to view status and upload documents"} onChange={(e) => setUiRequirements(e.target.value)} rows={4} className="w-full border px-3 py-2 rounded" />
                  </div>

                  <div className="mt-4 flex items-center gap-3">
                    <button onClick={async () => { const res = await onSave(true); if (res && res.success) setShowModal(false); }} className="px-6 py-2 bg-blue-600 text-white rounded">Submit</button>
                    <button onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="p-4">
            <p className="mb-3">Open the Requirement Detail to view or edit the requirement in a popup.</p>
            <div className="flex flex-col gap-3">
              {/* <div className="flex items-center gap-3">
                {savedFileUrl && (
                  <a href={savedFileUrl} target="_blank" rel="noopener noreferrer">
                    <button className="px-4 py-2 bg-gray-800 text-white rounded">Download</button>
                  </a>
                )}

                {quotationUrl && (
                  <a href={quotationUrl} target="_blank" rel="noopener noreferrer">
                    <button className="px-4 py-2 bg-green-600 text-white rounded">Download Quotation</button>
                  </a>
                )}
              </div> */}

              <div>
                <h3 className="font-semibold mb-2">Your saved requirements</h3>
                {customerRequirements.length === 0 ? (
                  <div className="text-sm text-gray-500">No saved requirements found.</div>
                ) : (
                  <ul className="space-y-2 max-h-48 overflow-y-auto">
                    {customerRequirements.map((r) => (
                      <li key={r._id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div>
                          <div className="font-medium">{r.heading || 'Untitled'}</div>
                          <div className="text-xs text-gray-500">{r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          {r.filePath && (
                            <a href={(process.env.REACT_APP_BACKEND_URL || '') + r.filePath} target="_blank" rel="noopener noreferrer">
                              <button className="px-3 py-1 bg-gray-200 rounded text-sm">PDF</button>
                            </a>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            </div>
          </div>
      
      )}
    </div>
  );
}